/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import PerfectScrollbar from 'react-perfect-scrollbar';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/styles';
import styles from '../../../style';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  CircularProgress,
  Tooltip,
} from '@material-ui/core';
import {
  CheckOutlined,
  ClearOutlined,
  Edit,
  DeleteOutline,
} from '@material-ui/icons';
import { connect } from 'react-redux';
import {
  saveSocialHistory,
  updateSocialHistory,
  deleteSocialHistory,
  getPatientMedicalHistory,
} from 'services/soap/action';

// ---------------------------------------------------------------------------
// TipButton: IconButton wrapped in a Tooltip.
// The <span> wrapper ensures the tooltip renders even when the button is disabled.
// ---------------------------------------------------------------------------
const TipButton = ({ title, children, ...rest }) => (
  <Tooltip title={title} arrow>
    <span style={{ display: 'inline-flex' }}>
      <IconButton {...rest}>{children}</IconButton>
    </span>
  </Tooltip>
);

const SocialHistory = props => {
  const { classes, patientId, ptMedicalHistory, getPatientMedicalHistory } = props;

  const initialUserState = { id: 0, description: '' };

  const [values, setValues] = useState(initialUserState);
  const [editvalues, setEditValues] = useState(initialUserState);
  const [SH, setSH] = useState([]);
  const [isEdit, setIsEdit] = useState(false);

  // ---------------------------------------------------------------------------
  // Loading states
  // 'save'        — header row save button
  // 'update'      — inline update confirm button
  // `delete-{id}` — delete spinner on that specific row
  // ---------------------------------------------------------------------------
  const [loadingId, setLoadingId] = useState(null);

  // ---------------------------------------------------------------------------
  // Sync Redux → local state
  // Guard removed so an empty array properly clears local state too
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (ptMedicalHistory?.socialHistory !== undefined)
      setSH(ptMedicalHistory.socialHistory || []);
  }, [ptMedicalHistory]);

  // ---------------------------------------------------------------------------
  // Field handlers
  // ---------------------------------------------------------------------------
  const handleFieldChange = (field, value) =>
    setValues({ ...values, [field]: value });

  const handleEditFieldChange = (field, value) =>
    setEditValues({ ...editvalues, [field]: value });

  // ---------------------------------------------------------------------------
  // Save new social history
  // ---------------------------------------------------------------------------
  const handleSaveSocialHistory = async () => {
    if (values.description) {
      setLoadingId('save');
      try {
        const res = await props.saveSocialHistory(patientId, values);
        if (res.status) {
          setValues(initialUserState);
          getPatientMedicalHistory(patientId);
        }
      } finally {
        setLoadingId(null);
      }
    }
  };

  // ---------------------------------------------------------------------------
  // Update existing social history
  // ---------------------------------------------------------------------------
  const handleUpdateSocialHistory = async idx => {
    if (editvalues.description) {
      setLoadingId('update');
      try {
        const res = await props.updateSocialHistory(patientId, editvalues);
        if (res.status) {
          getPatientMedicalHistory(patientId);
          setIsEdit(false);
        }
      } finally {
        setLoadingId(null);
      }
    }
  };

  // ---------------------------------------------------------------------------
  // Delete social history
  // ---------------------------------------------------------------------------
  const handleDeleteSocialHistory = async id => {
    if (loadingId !== null) return;
    setLoadingId(`delete-${id}`);
    try {
      const res = await props.deleteSocialHistory(patientId, id);
      if (res.status) {
        getPatientMedicalHistory(patientId);
        setValues(initialUserState);
      }
    } finally {
      setLoadingId(null);
    }
  };

  // ---------------------------------------------------------------------------
  // Edit / clear
  // ---------------------------------------------------------------------------
  const handleEditSocialHistory = (data, index) => {
    setIsEdit(true);
    if (data)
      setEditValues({
        id: data.id,
        description: data.description,
        editIndex: index,
      });
  };

  const clearSocialHistory = () => {
    setIsEdit(false);
    setValues(initialUserState);
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className={classes.templateWrap}>
      <div className={classes.templateBody} style={{ borderTop: 'default' }}>
        <PerfectScrollbar>
          <div style={{ maxHeight: 'calc(100vh - 298px)' }}>
            <Table className={classes.templateTable}>

              {/* ============================================================
                  HEADER ROW — add new social history
              ============================================================ */}
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TextField
                      className={classes.textBox}
                      InputProps={{ classes: { notchedOutline: classes.notchedOutline } }}
                      onChange={e => handleFieldChange('description', e.target.value)}
                      placeholder="Input description"
                      value={values.description}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell width="10%" align="center" style={{ whiteSpace: 'nowrap' }}>
                    <TipButton
                      title="Save"
                      disabled={loadingId === 'save'}
                      onClick={handleSaveSocialHistory}
                      size="small"
                      style={{ color: '#03A484' }}>
                      {loadingId === 'save'
                        ? <CircularProgress size={16} color="inherit" />
                        : <CheckOutlined />}
                    </TipButton>
                    <TipButton
                      title="Clear"
                      disabled={loadingId === 'save'}
                      onClick={clearSocialHistory}
                      size="small">
                      <ClearOutlined style={{ color: '#DE5555' }} />
                    </TipButton>
                  </TableCell>
                </TableRow>
              </TableHead>

              {/* ============================================================
                  BODY — existing social history rows
              ============================================================ */}
              <TableBody>
                {SH &&
                  SH.length > 0 &&
                  SH.map((item, idx) => (
                    <TableRow key={item.id}>
                      {isEdit && idx === editvalues.editIndex ? (
                        <TableCell>
                          <TextField
                            className={classes.textBox}
                            InputProps={{ classes: { notchedOutline: classes.notchedOutline } }}
                            onChange={e => handleEditFieldChange('description', e.target.value)}
                            placeholder="Input description"
                            value={editvalues.description}
                            variant="outlined"
                          />
                        </TableCell>
                      ) : (
                        <TableCell>{item.description}</TableCell>
                      )}
                      <TableCell align="center" width={72}>
                        {isEdit && idx === editvalues.editIndex ? (
                          <>
                            <TipButton
                              title="Edit"
                              disabled={loadingId === 'update'}
                              onClick={() => handleUpdateSocialHistory(idx)}
                              size="small"
                              style={{ color: '#03A484' }}>
                              {loadingId === 'update'
                                ? <CircularProgress size={16} color="inherit" />
                                : <CheckOutlined />}
                            </TipButton>
                            <TipButton
                              title="Clear"
                              disabled={loadingId === 'update'}
                              onClick={clearSocialHistory}
                              size="small">
                              <ClearOutlined style={{ color: '#DE5555' }} />
                            </TipButton>
                          </>
                        ) : (
                          <>
                            <TipButton
                              title="Edit"
                              disabled={loadingId !== null}
                              onClick={() => handleEditSocialHistory(item, idx)}
                              size="small">
                              <Edit color="primary" />
                            </TipButton>
                            <TipButton
                              title="Delete"
                              disabled={loadingId !== null}
                              onClick={() => handleDeleteSocialHistory(item.id)}
                              size="small">
                              {loadingId === `delete-${item.id}`
                                ? <CircularProgress size={16} color="inherit" />
                                : <DeleteOutline style={{ color: '#DE5555' }} />}
                            </TipButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        </PerfectScrollbar>
      </div>
    </div>
  );
};

SocialHistory.propTypes = {
  className: PropTypes.string,
};

const mapDispatchToProps = {
  saveSocialHistory,
  updateSocialHistory,
  deleteSocialHistory,
  getPatientMedicalHistory,
};

const mapStateToProps = state => ({
  ptMedicalHistory: state.soap && state.soap.ptMedicalHistory,
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(SocialHistory))
);